import React, { useState, useEffect } from 'react';
import AdminLayout, { adminFetch, KpiCard, SectionTitle, AdminCard, Badge, useAdmin } from './AdminLayout';

export default function AdminAides() {
  const [data, setData]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [running, setRunning]   = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'error'|'success', message }

  const load = () => {
    setLoading(true);
    setLoadError(false);
    adminFetch('/api/admin/aides')
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const triggerUpdate = async () => {
    setRunning(true); setFeedback(null);
    try {
      const res = await adminFetch('/api/admin/run-job/update-db', { method: 'POST' });
      const d = await res.json();
      if (d.success) {
        setFeedback({ type: 'success', message: `Mise à jour lancée le ${new Date(d.timestamp).toLocaleString('fr-FR')}` });
      } else {
        setFeedback({ type: 'error', message: d.error || 'Impossible de lancer la mise à jour. Réessayez dans un instant.' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Erreur de communication avec le serveur. Vérifiez votre connexion.' });
    }
    setRunning(false);
  };

  return (
    <AdminLayout title="Base de données aides">
      <Content data={data} loading={loading} loadError={loadError} running={running} feedback={feedback} onUpdate={triggerUpdate} onRetry={load} />
    </AdminLayout>
  );
}

function Content({ data, loading, loadError, running, feedback, onUpdate, onRetry }) {
  const { dark, border } = useAdmin();
  const textColor  = dark ? '#e0e0e0' : '#2D2D2D';
  const mutedColor = '#8A8A8A';

  if (loading) return <p style={{ color: mutedColor, padding: 16 }}>Chargement…</p>;
  if (loadError || !data) {
    return (
      <div style={{
        padding: 16, borderRadius: 10, background: '#FDECEA',
        border: '1px solid rgba(192,57,43,0.3)', color: '#933',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <span>Erreur de chargement. Vérifiez votre connexion.</span>
        <button onClick={onRetry} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none',
          background: '#D4724A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Réessayer</button>
      </div>
    );
  }

  const { stats, non_verifies, update_lines } = data;
  const hasAlerts = stats.non_verifies > 0;

  return (
    <div>
      {/* Alert */}
      {hasAlerts && (
        <div style={{
          background: '#FEF3C7', border: '1px solid rgba(217,119,6,0.4)',
          borderRadius: 12, padding: '14px 20px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 14,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <strong style={{ color: '#D97706' }}>
              {stats.non_verifies} aide{stats.non_verifies > 1 ? 's' : ''} non vérifiée{stats.non_verifies > 1 ? 's' : ''} depuis plus de 15 jours
            </strong>
            <div style={{ color: '#5A5A5A', marginTop: 2 }}>
              Effectuez une mise à jour pour maintenir la base à jour.
            </div>
          </div>
        </div>
      )}

      {/* Stats KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <KpiCard label="Total aides" value={stats.total} icon="📋" color="#D4724A" />
        <KpiCard label="Actives" value={stats.actif} icon="✅" color="#3D8B5E" />
        <KpiCard label="Inactives" value={stats.inactif} icon="⏸️" color="#8A8A8A" />
        <KpiCard label="Non vérifiées +15j" value={stats.non_verifies} icon="⚠️" color={hasAlerts ? '#D97706' : '#3D8B5E'} />
      </div>

      {/* Bouton mise à jour */}
      <AdminCard style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600, color: textColor, marginBottom: 4 }}>Mise à jour manuelle</div>
          <div style={{ fontSize: 13, color: mutedColor }}>Lance immédiatement la vérification des sources officielles.</div>
          {feedback && (
            <div style={{
              marginTop: 8, fontSize: 13, fontWeight: 500,
              color: feedback.type === 'error' ? '#933' : '#3D8B5E',
            }}>
              {feedback.type === 'error' ? '⚠ ' : '✓ '}{feedback.message}
            </div>
          )}
        </div>
        <button onClick={onUpdate} disabled={running} style={{
          padding: '12px 28px', borderRadius: 12, border: 'none',
          background: running ? '#D9D9D9' : '#D4724A',
          color: running ? '#8A8A8A' : '#fff',
          fontSize: 15, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
        }}>
          {running ? 'En cours…' : 'Lancer mise à jour maintenant'}
        </button>
      </AdminCard>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Aides non vérifiées */}
        <AdminCard>
          <SectionTitle>Aides à vérifier</SectionTitle>
          {non_verifies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <p style={{ color: mutedColor, fontSize: 14 }}>Toutes les aides sont à jour !</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${border}` }}>
                    {['Nom de l\'aide', 'Dernière vérif.', 'Source'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: mutedColor, fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {non_verifies.map((a, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                      <td style={{ padding: '8px', color: textColor, fontWeight: 500 }}>{a.nom || '—'}</td>
                      <td style={{ padding: '8px' }}>
                        <Badge color="#D97706">
                          {a.derniere_verification ? new Date(a.derniere_verification).toLocaleDateString('fr-FR') : 'Jamais'}
                        </Badge>
                      </td>
                      <td style={{ padding: '8px', color: mutedColor, fontSize: 11 }}>
                        {a.source ? (
                          <a href={a.source} target="_blank" rel="noopener noreferrer" style={{ color: '#2980B9' }}>
                            Lien
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminCard>

        {/* Journal des mises à jour */}
        <AdminCard>
          <SectionTitle>Journal updates-log.md</SectionTitle>
          {update_lines.length === 0 ? (
            <p style={{ fontSize: 14, color: mutedColor }}>Aucune mise à jour enregistrée dans updates-log.md.</p>
          ) : (
            <div style={{
              background: dark ? '#0f0f23' : '#F5F3F0', borderRadius: 8, padding: '12px 16px',
              fontFamily: 'monospace', fontSize: 12, maxHeight: 400, overflowY: 'auto',
              lineHeight: 1.7, color: dark ? '#a8c9b5' : '#3F6B51',
            }}>
              {update_lines.map((l, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${dark ? '#1a1a2e' : '#E0DDD8'}`, paddingBottom: 4, marginBottom: 4 }}>
                  {l}
                </div>
              ))}
            </div>
          )}
        </AdminCard>
      </div>
    </div>
  );
}
